"use client";

import SkeletonLoader from "@/components/SkeletonLoader";
import { HomepageAgentSummary } from "@/components/seo/HomepageAgentSummary";
import type { AppShellLayout } from "@/lib/appShellLayout";
import { type AppBrandConfig, DEFAULT_APP_BRAND } from "@/lib/brands";
import { createLogger } from "@/lib/logger";
import {
  buildPracticeSentenceInstructions,
  buildPracticeWordInstructions,
  buildTranslateSentenceInstructions,
} from "@/lib/practiceSentenceUrl";
import { getPracticeScenarioPresetById } from "@/lib/seo/practiceScenarioPresets";
import { type SessionLaunchPreset } from "@/lib/sessionLaunchPresets";
import { useSearchParams } from "next/navigation";
import React, { Suspense, useState } from "react";
import MainAppView from "../../app/Fullscreen";
import { PlayerProvider, usePlayer } from "../../app/player/PlayerContext";
import { TranscriptSegment } from "../../models/podcast";

const logger = createLogger("LanguageLearningApp");

interface LanguageLearningAppProps {
  children?: React.ReactNode;
  brand?: AppBrandConfig;
  enableWebappSignupBeforeDemoExperiment?: boolean;
  showGooglePlayBadge?: boolean;
  showHomepageSuspenseFallback?: boolean;
  initialTargetLanguageParam?: string | null;
  initialInstructionLanguageParam?: string | null;
  initialLanguageLevelParam?: string | null;
  initialSessionModeParam?: string | null;
  initialJourneyChapterIdParam?: string | null;
  initialPodcastDiscussionIdParam?: string | null;
  initialCustomInstructionsParam?: string | null;
  launchPreset?: SessionLaunchPreset;
  shellLayout?: AppShellLayout;
}

const MainContent = ({
  children,
  brand,
  enableWebappSignupBeforeDemoExperiment,
  initialInstructionLanguageParam,
  initialLanguageLevelParam,
  initialTargetLanguageParam,
  initialSessionModeParam,
  initialJourneyChapterIdParam,
  initialPodcastDiscussionIdParam,
  initialCustomInstructionsParam,
  launchPreset,
  shellLayout,
  showGooglePlayBadge,
}: {
  children?: React.ReactNode;
  brand: AppBrandConfig;
  enableWebappSignupBeforeDemoExperiment?: boolean;
  initialInstructionLanguageParam?: string | null;
  initialLanguageLevelParam?: string | null;
  initialTargetLanguageParam?: string | null;
  initialSessionModeParam?: string | null;
  initialJourneyChapterIdParam?: string | null;
  initialPodcastDiscussionIdParam?: string | null;
  initialCustomInstructionsParam?: string | null;
  launchPreset?: SessionLaunchPreset;
  shellLayout?: AppShellLayout;
  showGooglePlayBadge?: boolean;
}) => {
  const searchParams = useSearchParams();
  const { currentTime, currentSegment, isPlaying, togglePlay, seekToSegment, segments } =
    usePlayer();

  const [showTranslation, setShowTranslation] = useState(false);
  const [interactionMode, setInteractionMode] = useState<"passive" | "text" | "voice">("passive");
  const [awaitingUserResponse, setAwaitingUserResponse] = useState(false);
  const scenarioPreset = getPracticeScenarioPresetById(searchParams.get("scenario"));
  const targetLanguageParam =
    launchPreset?.targetLanguageCode ||
    initialTargetLanguageParam ||
    searchParams.get("target") ||
    searchParams.get("languageCode") ||
    searchParams.get("language") ||
    scenarioPreset?.languageCode;
  const instructionLanguageParam =
    launchPreset?.instructionLanguageCode ||
    initialInstructionLanguageParam ||
    searchParams.get("instruction") ||
    searchParams.get("instructionLanguage") ||
    searchParams.get("fromLanguageCode");
  const languageLevelParam =
    launchPreset?.languageLevel ||
    initialLanguageLevelParam ||
    searchParams.get("languageLevel") ||
    searchParams.get("level") ||
    searchParams.get("selectedLanguageLevel") ||
    scenarioPreset?.level;
  const sessionModeParam =
    launchPreset?.runtimeSessionMode ||
    initialSessionModeParam ||
    searchParams.get("sessionMode") ||
    (scenarioPreset ? "conversation" : null);
  const journeyChapterIdParam = launchPreset
    ? null
    : initialJourneyChapterIdParam ||
      searchParams.get("journeyChapterId") ||
      searchParams.get("chapterId");
  const podcastDiscussionIdParam = launchPreset
    ? null
    : initialPodcastDiscussionIdParam ||
      searchParams.get("podcastDiscussionId") ||
      searchParams.get("podcastId");
  const practiceSentenceInstructions = buildPracticeSentenceInstructions(
    searchParams.get("practiceSentence")
  );
  const practiceWordInstructions = buildPracticeWordInstructions(searchParams.get("practiceWord"));
  const translateSentenceInstructions = buildTranslateSentenceInstructions(
    searchParams.get("translateSentence")
  );
  const enterprisePresetParam = searchParams.get("enterprisePreset");
  const customInstructionsParam = launchPreset
    ? launchPreset.customInstructions ||
      (launchPreset.id === "acquisition-speaking-demo"
        ? scenarioPreset?.instructions || null
        : null)
    : initialCustomInstructionsParam ||
      practiceWordInstructions ||
      practiceSentenceInstructions ||
      translateSentenceInstructions ||
      (enterprisePresetParam === "restaurant"
        ? searchParams.get("customInstructions") || searchParams.get("custom_instructions")
        : null) ||
      scenarioPreset?.instructions ||
      null;
  const initialCustomInstructionsLifetime =
    !launchPreset && !initialCustomInstructionsParam && practiceWordInstructions
      ? "route-scoped"
      : "persistent";

  const handleSegmentClick = (index: number) => {
    seekToSegment(index);
  };

  const handleExplainSegment = (segment: TranscriptSegment) => {
    logger.info("Explaining segment (placeholder):", segment);
  };

  const getSegmentClasses = (segment: TranscriptSegment, _index: number): string => {
    const isActive = currentSegment?.id === segment.id;

    if (isActive) {
      return "bg-orange-500/20 border border-orange-500/40 text-white";
    }

    return "hover:bg-gray-800/50";
  };

  return (
    <MainAppView
      brand={brand}
      segments={segments}
      currentTime={currentTime}
      currentSegment={currentSegment}
      showTranslation={showTranslation}
      interactionMode={interactionMode}
      isPlaying={isPlaying}
      togglePlay={togglePlay}
      onSegmentClick={handleSegmentClick}
      onExplainSegment={handleExplainSegment}
      setShowTranslation={setShowTranslation}
      setInteractionMode={setInteractionMode}
      getSegmentClasses={getSegmentClasses}
      awaitingUserResponse={awaitingUserResponse}
      initialTargetLanguageParam={targetLanguageParam}
      initialInstructionLanguageParam={instructionLanguageParam}
      initialLanguageLevelParam={languageLevelParam}
      initialSessionModeParam={sessionModeParam}
      initialJourneyChapterIdParam={journeyChapterIdParam}
      initialPodcastDiscussionIdParam={podcastDiscussionIdParam}
      initialCustomInstructionsParam={customInstructionsParam}
      initialCustomInstructionsLifetime={initialCustomInstructionsLifetime}
      preSessionVariant={launchPreset?.preSessionVariant}
      analyticsSurface={launchPreset?.analyticsSurface}
      embedAnalyticsEventPrefix={launchPreset?.analyticsEventPrefix}
      enableFreemiumPreSessionOffer={launchPreset?.enableFreemiumPreSessionOffer}
      prewarmPolicy={launchPreset?.prewarmPolicy}
      launchSurface={launchPreset?.launchSurface}
      preferenceStorage={launchPreset?.preferenceStorage}
      shellLayout={shellLayout}
      showGooglePlayBadge={showGooglePlayBadge}
      enableWebappSignupBeforeDemoExperiment={enableWebappSignupBeforeDemoExperiment}
    >
      {children}
    </MainAppView>
  );
};

export function LanguageLearningApp({
  children,
  brand = DEFAULT_APP_BRAND,
  enableWebappSignupBeforeDemoExperiment = false,
  initialInstructionLanguageParam,
  initialLanguageLevelParam,
  initialTargetLanguageParam,
  initialSessionModeParam,
  initialJourneyChapterIdParam,
  initialPodcastDiscussionIdParam,
  initialCustomInstructionsParam,
  launchPreset,
  shellLayout,
  showGooglePlayBadge = false,
  showHomepageSuspenseFallback = false,
}: LanguageLearningAppProps) {
  return (
    <PlayerProvider>
      <Suspense
        fallback={
          showHomepageSuspenseFallback ? (
            <>
              <SkeletonLoader />
              <HomepageAgentSummary />
            </>
          ) : null
        }
      >
        <MainContent
          brand={brand}
          enableWebappSignupBeforeDemoExperiment={enableWebappSignupBeforeDemoExperiment}
          initialInstructionLanguageParam={initialInstructionLanguageParam}
          initialLanguageLevelParam={initialLanguageLevelParam}
          initialTargetLanguageParam={initialTargetLanguageParam}
          initialSessionModeParam={initialSessionModeParam}
          initialJourneyChapterIdParam={initialJourneyChapterIdParam}
          initialPodcastDiscussionIdParam={initialPodcastDiscussionIdParam}
          initialCustomInstructionsParam={initialCustomInstructionsParam}
          launchPreset={launchPreset}
          shellLayout={shellLayout}
          showGooglePlayBadge={showGooglePlayBadge}
        >
          {children}
        </MainContent>
      </Suspense>
    </PlayerProvider>
  );
}
